import React from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';

import { useStyles } from './ExecutionSummary.styles';

import CompletionIcon from '@assets/completion-icon.svg';
import StepsIcon from '@assets/steps-icon.svg';
import ErrorsIcon from '@assets/errors-icon.svg';
import TimeIcon from '@assets/time-icon.svg';

import { useExecutionsUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import { ExecutionSummaryGraph } from '../ExecutionSummaryGraph';
import { CircularProgress } from '@mui/material';
import { isErrorEventType } from 'NeoAnalytics/executionSteps';

const formatSummaryDataLabel = (key: string) =>
    key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase());

export function timeFormatterDuration(value: number | undefined): string {
    const s = Math.max(0, Math.floor(value!));

    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;

    const pad2 = (n: number) => String(n).padStart(2, "0");

    return hours > 0 ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}` : `${pad2(minutes)}:${pad2(seconds)}`;
}

export function formatDuration(s: string): string {
    if(s){
        const m = s.match(/^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
        if (!m) {throw new Error(`Invalid duration format: ${s}`);}

        const hours = Number(m[1]);
        const minutes = Number(m[2]);
        const seconds = Number(m[3]);

        const frac = m[4] ?? "0";
        const fractionalSeconds = Number(`${frac.slice(0,2)}`);

        const pad2 = (n: number) => String(n).padStart(2, "0");

        return hours > 0
            ? `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}.${pad2(fractionalSeconds)}`
            : `${pad2(minutes)}:${pad2(seconds)}.${pad2(fractionalSeconds)}`;
    }else{
        return "Not Completed";
    }
}

export const ExecutionSummary: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const executionsUIStore = useExecutionsUIStore();

    return (
        <>
            <Observer>
                {() =>{
                    const data = executionsUIStore.execution;

                    const steps = data?.steps;

                    const completionRate = (((steps?.filter(s => s.endTime).length ?? 0) / (steps?.length ?? 0)) * 100).toFixed(0);
                    const errorCount = data?.events.filter(e => isErrorEventType(e.type)).length ?? 0;
                    const summaryDataItems = executionsUIStore.summaryDataItems;

                    return (
                        <div className={styles.summaryContainer}>
                            <div className={styles.summaryHeader}>
                                <div className={styles.summaryItem}>
                                    <div className={styles.summaryImageContainer}>
                                        <img src={CompletionIcon} className={styles.summaryItemImage}/>
                                    </div>
                                    <div className={styles.summaryTextContainer}>
                                        <div className={styles.summaryTitle}>
                                                Completion Rate
                                        </div>
                                        <div className={styles.summaryValue}>
                                            {(steps?.length ?? 0) > 0 ? `${completionRate}%` : '0%'}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.summaryItem}>
                                    <div className={styles.summaryImageContainer}>
                                        <img src={StepsIcon} className={styles.summaryItemImage}/>
                                    </div>
                                    <div className={styles.summaryTextContainer}>
                                        <div className={styles.summaryTitle}>
                                                Number of Steps
                                        </div>
                                        <div className={styles.summaryValue}>
                                            {data?.steps.filter(s => s.endTime).length}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.summaryItem}>
                                    <div className={styles.summaryImageContainer}>
                                        <img src={ErrorsIcon} className={styles.summaryItemImage}/>
                                    </div>
                                    <div className={styles.summaryTextContainer}>
                                        <div className={styles.summaryTitle}>
                                                Error count
                                        </div>
                                        <div className={styles.summaryValue}>
                                            {errorCount}
                                        </div>
                                    </div>
                                </div>
                                <div className={styles.summaryItem}>
                                    <div className={styles.summaryImageContainer}>
                                        <img src={TimeIcon} className={styles.summaryItemImage}/>
                                    </div>
                                    <div className={styles.summaryTextContainer}>
                                        <div className={styles.summaryTitle}>
                                                Total execution time
                                        </div>
                                        <div className={styles.summaryValue}>
                                            {data?.endTime ? formatDuration(data.duration!) : "Not Completed"}
                                        </div>
                                    </div>
                                </div>
                                {summaryDataItems.map(item => (
                                    <div key={item.key} className={styles.summaryItem}>
                                        <div className={styles.summaryImageContainer}>
                                            <div className={styles.summaryItemMonogram}>
                                                {item.key.replace(/\s+/g, '').slice(0, 2).toUpperCase()}
                                            </div>
                                        </div>
                                        <div className={styles.summaryTextContainer}>
                                            <div className={styles.summaryTitle}>
                                                {formatSummaryDataLabel(item.key)}
                                            </div>
                                            <div className={styles.summaryValue}>
                                                {item.value}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            { executionsUIStore.status.isDone &&
                                    <ExecutionSummaryGraph />
                                    ||
                                    <CircularProgress className={styles.loader} color='inherit' size={50} />
                            }
                        </div>
                    )
                }}
            </Observer>
        </>
    )
}